import { BrowserRouter, Routes, Route, useParams, useNavigate, useLocation } from 'react-router-dom';
import React, { createContext, useContext, useEffect } from 'react';
import MainLayout from './components/MainLayout';
import { WebSocketProvider, useWebSocket } from './contexts/WebSocketContext';
import { ConversationsProvider } from './contexts/ConversationsContext';
import { useFlashMessagesContext } from './contexts/FlashMessagesContext';
import InsuranceSummariesPage from './pages/InsuranceSummariesPage';
import AdminCostsPage from './pages/AdminCostsPage';

// Type guard for crypto API
interface CryptoWithUUID {
  randomUUID(): string;
}

function hasCryptoUUID(obj: unknown): obj is CryptoWithUUID {
  return typeof obj === 'object' && obj !== null && 'randomUUID' in obj && typeof (obj as CryptoWithUUID).randomUUID === 'function';
}

// Generate UUID using crypto API with proper typing
const generateUUID = (): string => {
  const globalScope = globalThis as unknown;
  
  if (typeof globalScope === 'object' && globalScope !== null && 'crypto' in globalScope) {
    const crypto = (globalScope as { crypto: unknown }).crypto;
    if (hasCryptoUUID(crypto)) {
      return crypto.randomUUID();
    }
  }
  
  // Fallback UUID generation if crypto.randomUUID is not available
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

// Context to pass conversation ID to components
interface RouteContextType {
  conversationId: string;
}

const RouteContext = createContext<RouteContextType | null>(null);

// Hook to get conversation ID from route context
export const useRouteContext = (): RouteContextType => {
  const context = useContext(RouteContext);
  if (!context) {
    throw new Error('useRouteContext must be used within a route component');
  }
  return context;
};

// Component that provides MainLayout with conversation context
interface MainLayoutWrapperProps {
  conversationId: string;
  signOut: () => void;
  insuranceReportId?: string;
  initialMessage?: string;
}

function MainLayoutWrapper({ conversationId, signOut, insuranceReportId, initialMessage }: MainLayoutWrapperProps) {
  const contextValue: RouteContextType = { conversationId };
  const { connectToConversation } = useWebSocket();

  // Connect to the conversation when conversationId changes
  useEffect(() => {
    connectToConversation(conversationId, insuranceReportId).catch(err => {
      console.error('Failed to connect to conversation:', err);
    });
  }, [conversationId, insuranceReportId, connectToConversation]);

  return (
    <RouteContext.Provider value={contextValue}>
      <MainLayout onSignOut={signOut} conversationId={conversationId} initialMessage={initialMessage} />
    </RouteContext.Provider>
  );
}

// Home component - redirects to new conversation
interface HomeComponentProps {
  signOut: () => void;
}

function HomeComponent({ signOut: _signOut }: HomeComponentProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { addMessage } = useFlashMessagesContext();
  
  useEffect(() => {
    // Check for signup success parameter
    const urlParams = new URLSearchParams(location.search);
    const signupSuccess = urlParams.get('signup_success');
    
    if (signupSuccess === 'true') {
      // Show success message
      addMessage({
        id: generateUUID(),
        type: 'success',
        title: 'Welcome to Gekkovet Copilot!',
        message: 'Your account has been created successfully. Please log in to start using Copilot.',
        timeout: 8000, // Show for 8 seconds
        dismissible: true,
      });
      
      // Clean up URL by removing the query parameter
      const cleanUrl = `${location.pathname}`;
      window.history.replaceState({}, '', cleanUrl);
    }
    
    // Generate new conversation ID and redirect to it
    const newConversationId = generateUUID();
    console.log('Redirecting to new conversation:', newConversationId);
    navigate(`/c/${newConversationId}`, { replace: true });
  }, [navigate, location, addMessage]);
  
  // Show loading state while redirecting
  return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      height: '100vh',
      fontSize: '16px',
      color: '#666'
    }}>
      Starting new conversation...
    </div>
  );
}

// Conversation component - provides existing conversation ID
interface ConversationComponentProps {
  signOut: () => void;
}

function ConversationComponent({ signOut }: ConversationComponentProps) {
  const { conversationId } = useParams<{ conversationId: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  if (!conversationId) {
    throw new Error('conversationId parameter is required');
  }

  // Read insurance context from location state (passed from insurance results page)
  const locationState = location.state as { insuranceReportId?: string; initialMessage?: string } | null;

  // Clear location state after reading it to prevent it from persisting across page refreshes
  // This prevents the initial message from being sent again on refresh
  useEffect(() => {
    if (locationState?.initialMessage || locationState?.insuranceReportId) {
      // Replace current history entry without the state
      navigate(location.pathname, { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount - we intentionally read locationState once and clear it

  return (
    <MainLayoutWrapper
      conversationId={conversationId}
      signOut={signOut}
      insuranceReportId={locationState?.insuranceReportId}
      initialMessage={locationState?.initialMessage}
    />
  );
}

// Router provider component
interface AppRouterProps {
  signOut: () => void;
}

export const AppRouter: React.FC<AppRouterProps> = ({ signOut }) => {
  return (
    <WebSocketProvider>
      <ConversationsProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<HomeComponent signOut={signOut} />} />
            <Route path="/c/:conversationId" element={<ConversationComponent signOut={signOut} />} />

            {/* Secret routes - not in navigation */}
            <Route path="/insurance-summaries" element={<InsuranceSummariesPage signOut={signOut} />} />
            <Route path="/insurance-summaries/:reportId" element={<InsuranceSummariesPage signOut={signOut} />} />
            <Route path="/admin/costs" element={<AdminCostsPage signOut={signOut} />} />
          </Routes>
        </BrowserRouter>
      </ConversationsProvider>
    </WebSocketProvider>
  );
};

// Hook to get current conversation ID from router state
export const useCurrentConversationId = (): string => {
  try {
    const context = useRouteContext();
    return context.conversationId;
  } catch {
    // Fallback if not within router context
    return generateUUID();
  }
};

// Navigation functions using React Router's useNavigate hook
export const useRouter = () => {
  const navigate = useNavigate();
  
  return {
    navigateToConversation: (conversationId: string) => {
      navigate(`/c/${conversationId}`);
    },
    navigateToHome: () => {
      navigate('/');
    }
  };
};
